import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  alpha,
  useTheme,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  SelectChangeEvent,
  FormHelperText,
  Switch,
  FormControlLabel,
  Alert,
  Snackbar,
  Divider,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import jobseekerapi from '../../../../services/jobseekerApi';

// Types
interface Module {
  _id: string;
  module_title: string;
}

interface Plan {
  _id: string;
  title: string;
  module_ids: string[];
  base_price: number;
  discount_type: 'none' | 'percentage' | 'fixed';
  discount_value: number;
  discounted_price: number;
  status: string;
  is_free_plan: boolean;
  is_any_limit: boolean;
  search_limit: number;
  resume_limit: number;
  job_apply_limit: number;
  auto_apply_limit: number;
  createdAt?: string;
  updatedAt: string;
  __v: number;
}

interface PlanFormData {
  title: string;
  module_ids: string[];
  base_price: number;
  discount_type: 'none' | 'percentage' | 'fixed';
  discount_value: number;
  is_free_plan: boolean;
  is_any_limit: boolean;
  search_limit: number;
  resume_limit: number;
  job_apply_limit: number;
  auto_apply_limit: number;
  cover_letter_limit: number
}

interface AlertState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

const initialFormData: PlanFormData = {
  title: '',
  module_ids: [],
  base_price: 0,
  discount_type: 'none',
  discount_value: 0,
  is_free_plan: true,
  is_any_limit: false,
  search_limit: 0,
  resume_limit: 0,
  job_apply_limit: 0,
  auto_apply_limit: 0,
  cover_letter_limit: 0
};

const Plans: React.FC = () => {
  const theme = useTheme();

  // State variables
  const [plans, setPlans] = useState<Plan[]>([]);
  const [modules, setModules] = useState<Module[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [planToDelete, setPlanToDelete] = useState<Plan | null>(null);
  const [formDialogOpen, setFormDialogOpen] = useState<boolean>(false);
  const [viewModalOpen, setViewModalOpen] = useState<boolean>(false);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [currentPlanId, setCurrentPlanId] = useState<string | null>(null);
  const [formData, setFormData] = useState<PlanFormData>(initialFormData);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [submitLoader, setSubmitLoader] = useState<any>(false);
  const [alert, setAlert] = useState<AlertState>({
    open: false,
    message: '',
    severity: 'info',
  });

  // Fetch plans on component mount and when filters/pagination changes
  useEffect(() => {
    fetchPlans();
  }, [page, rowsPerPage, searchQuery]);

  // Fetch modules on component mount
  useEffect(() => {
    fetchModules();
  }, []);

  // Automatically set is_free_plan when final price is 0
  useEffect(() => {
    const finalPrice = calculateFinalPrice(
      formData.base_price,
      formData.discount_type,
      formData.discount_value
    );

    if (finalPrice === 0 && !formData.is_free_plan) {
      setFormData((prev) => ({
        ...prev,
        is_free_plan: true,
      }));
    } else if (finalPrice > 0 && formData.is_free_plan) {
      setFormData((prev) => ({
        ...prev,
        is_free_plan: false,
      }));
    }
  }, [formData.base_price, formData.discount_type, formData.discount_value]);

  // Function to fetch plans
  const fetchPlans = async () => {
    setLoading(true);
    setError(null);

    try {
      // startToken is page number (1-indexed), endToken is number of records per page
      const startToken = page + 1;
      const endToken = rowsPerPage;

      const response = await jobseekerapi.post('/plan/getPlansList', {
        startToken,
        endToken,
        search: searchQuery,
      });

      if (response?.data?.status) {
        setPlans(response.data.data || []);
        setTotalRows(response.data.record_count || 0);
      } else {
        setError('Failed to fetch plans');
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to fetch plans');
      console.error('Error fetching plans:', err);
    } finally {
      setLoading(false);
    }
  };

  // Function to fetch modules for the dropdown
  const fetchModules = async () => {
    try {
      const response = await jobseekerapi.get('/user/modulesList');
      if (response?.data?.status) {
        setModules(response.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching modules:', err);
    }
  };

  // Handle page change
  const handleChangePage = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle search input change
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  // Reset search
  const resetFilters = () => {
    setSearchQuery('');
    setPage(0);
  };

  // Handle view details
  const handleViewDetails = (plan: Plan) => {
    setSelectedPlan(plan);
    setViewModalOpen(true);
  };

  // Handle close view modal
  const handleCloseViewModal = () => {
    setViewModalOpen(false);
    setSelectedPlan(null);
  };

  // Format date
  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Render status chip
  const renderStatusChip = (status: string) => {
    const statusLower = status?.toLowerCase();
    let color: 'success' | 'error' | 'warning' | 'default' = 'default';

    switch (statusLower) {
      case 'active':
        color = 'success';
        break;
      case 'inactive':
        color = 'error';
        break;
      case 'pending':
        color = 'warning';
        break;
      default:
        color = 'default';
    }

    return <Chip label={status} color={color} size="small" />;
  };

  // Open Add Plan Dialog
  const openAddDialog = () => {
    setFormData(initialFormData);
    setFormErrors({});
    setIsEditMode(false);
    setCurrentPlanId(null);
    setFormDialogOpen(true);
  };

  // Open Edit Plan Dialog
  const openEditDialog = async (plan: Plan) => {
    try {
      // Fetch plan details from API
      const response = await jobseekerapi.get(`/plan/getPlans/${plan._id}`);

      if (response?.data?.status) {
        const planData = response.data.data;

        // Extract module IDs from the module_ids array (which contains objects with _id and module_title)
        const moduleIds = planData.module_ids.map((module: any) => module._id);

        setFormData({
          title: planData.title,
          module_ids: moduleIds,
          base_price: planData.base_price,
          discount_type: planData.discount_type,
          discount_value: planData.discount_value,
          is_free_plan: planData.is_free_plan,
          is_any_limit: planData.is_any_limit,
          search_limit: planData.search_limit,
          resume_limit: planData.resume_limit,
          job_apply_limit: planData.job_apply_limit,
          auto_apply_limit: planData.auto_apply_limit,
          cover_letter_limit: planData.cover_letter_limit,
        });
        setFormErrors({});
        setIsEditMode(true);
        setCurrentPlanId(plan._id);
        setFormDialogOpen(true);
      } else {
        showAlert('Failed to fetch plan details', 'error');
      }
    } catch (err: any) {
      showAlert(err?.response?.data?.message || 'Failed to fetch plan details', 'error');
      console.error('Error fetching plan details:', err);
    }
  };

  // Close form dialog
  const closeFormDialog = () => {
    setFormDialogOpen(false);
    setFormData(initialFormData);
    setFormErrors({});
    setIsEditMode(false);
    setCurrentPlanId(null);
  };

  // Validate form
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.title.trim()) {
      errors.title = 'Title is required';
    }

    if (formData.module_ids.length === 0) {
      errors.module_ids = 'At least one module must be selected';
    }

    // if (formData.base_price <= 0) {
    //   errors.base_price = 'Base price must be greater than 0';
    // }

    // if (formData.discount_type !== 'none' && formData.discount_value <= 0) {
    //   errors.discount_value = 'Discount value must be greater than 0';
    // }

    if (formData.is_any_limit) {
      // if (formData.search_limit <= 0) {
      //   errors.search_limit = 'Search limit must be greater than 0';
      // }
      // if (formData.resume_limit <= 0) {
      //   errors.resume_limit = 'Resume limit must be greater than 0';
      // }
      // if (formData.job_apply_limit <= 0) {
      //   errors.job_apply_limit = 'Job apply limit must be greater than 0';
      // }
      // if (formData.auto_apply_limit <= 0) {
      //   errors.auto_apply_limit = 'Auto apply limit must be greater than 0';
      // }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form input change
  const handleFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string[]>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Handle number input change
  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: parseFloat(value) || 0,
    }));

    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Handle switch change
  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  // Handle Save Plan (Add or Edit)
  const handleSavePlan = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitLoader(true);
      if (isEditMode && currentPlanId) {
        // Update existing plan
        const response = await jobseekerapi.put('/plan/updatePlans', {
          id: currentPlanId,
          ...formData,
        });

        if (response?.data?.status) {
          showAlert('Plan updated successfully', 'success');
          fetchPlans();
          closeFormDialog();
        }
      } else {
        // Create new plan
        const response = await jobseekerapi.post('/plan/savePlans', formData);

        if (response?.data?.status) {
          showAlert('Plan created successfully', 'success');
          fetchPlans();
          closeFormDialog();
        }
      }
    } catch (err: any) {
      showAlert(err?.response?.data?.message || 'Failed to save plan', 'error');
      console.error('Error saving plan:', err);
    } finally {
      setSubmitLoader(false);
    }
  };

  // Open delete confirmation dialog
  const openDeleteDialog = (plan: Plan) => {
    setPlanToDelete(plan);
    setDeleteDialogOpen(true);
  };

  // Close delete confirmation dialog
  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setPlanToDelete(null);
  };

  // Handle delete plan
  const handleDeletePlan = async () => {
    if (!planToDelete?._id) return;

    try {
      const response = await jobseekerapi.delete(`/plan/deletePlans/${planToDelete._id}`);

      if (response?.data?.status) {
        showAlert('Plan deleted successfully', 'success');
        fetchPlans();
        closeDeleteDialog();
      }
    } catch (err: any) {
      showAlert(err?.response?.data?.message || 'Failed to delete plan', 'error');
      console.error('Error deleting plan:', err);
    }
  };

  // Show alert
  const showAlert = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
    setAlert({
      open: true,
      message,
      severity,
    });
  };

  // Close alert
  const handleCloseAlert = () => {
    setAlert((prev) => ({
      ...prev,
      open: false,
    }));
  };

  // Calculate final price
  const calculateFinalPrice = (basePrice: number, discountType: string, discountValue: number) => {
    if (discountType === 'none') return basePrice;
    if (discountType === 'percentage') {
      return basePrice - (basePrice * discountValue) / 100;
    }
    if (discountType === 'fixed') {
      return Math.max(0, basePrice - discountValue);
    }
    return basePrice;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography
          variant="h4"
          sx={{
            mb: 4,
            fontWeight: 700,
            color: theme.palette.primary.main,
            position: 'relative',
            display: 'inline-block',
            '&:after': {
              content: '""',
              position: 'absolute',
              bottom: -8,
              left: 0,
              width: 40,
              height: 4,
              backgroundColor: theme.palette.secondary.main,
              borderRadius: 2,
            },
          }}
        >
          Plans Management
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={openAddDialog}
          sx={{
            px: 3,
            boxShadow: `0 4px 8px ${alpha(theme.palette.primary.main, 0.2)}`,
          }}
        >
          Add Plan
        </Button>
      </Box>

      {/* Search Filter */}
      <Paper
        elevation={2}
        sx={{
          p: 2,
          mb: 3,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'center' },
          gap: 2,
        }}
      >
        <TextField
          label="Search Plans"
          variant="outlined"
          size="small"
          fullWidth
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ flexGrow: 1 }}
        />
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={resetFilters}>
          Reset
        </Button>
      </Paper>

      {/* Plans Table */}
      <Paper elevation={3}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Base Price</TableCell>
                <TableCell>Discount</TableCell>
                <TableCell>Discounted Price</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Last Updated</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={40} />
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3, color: 'error.main' }}>
                    {error}
                  </TableCell>
                </TableRow>
              ) : plans.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                    No plans found
                  </TableCell>
                </TableRow>
              ) : (
                plans.map((plan) => (
                  <TableRow key={plan._id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ fontWeight: 500 }}>{plan.title}</Typography>
                        {plan.is_free_plan && (
                          <Chip label="Free" size="small" color="info" variant="outlined" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontWeight: 500 }}>
                        ${plan.base_price.toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {plan.discount_type === 'none' ? (
                        <Chip label="No Discount" size="small" variant="outlined" />
                      ) : plan.discount_type === 'percentage' ? (
                        <Chip label={`${plan.discount_value}% off`} color="success" size="small" />
                      ) : (
                        <Chip label={`$${plan.discount_value} off`} color="success" size="small" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                        ${plan.discounted_price.toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {renderStatusChip(plan.status)}
                    </TableCell>
                    <TableCell>
                      {formatDate(plan.updatedAt)}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="View">
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={() => handleViewDetails(plan)}
                          sx={{
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.primary.main, 0.1),
                            },
                          }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton color="primary" size="small" onClick={() => openEditDialog(plan)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton color="error" size="small" onClick={() => openDeleteDialog(plan)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalRows}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Add/Edit Plan Dialog */}
      <Dialog open={formDialogOpen} onClose={closeFormDialog} maxWidth="md" fullWidth>
        <DialogTitle>{isEditMode ? 'Edit Plan' : 'Add New Plan'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            {/* Title */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Plan Title"
                name="title"
                value={formData.title}
                onChange={handleFormChange}
                error={!!formErrors.title}
                helperText={formErrors.title}
                required
              />
            </Grid>

            {/* Modules Selection */}
            <Grid item xs={12}>
              <FormControl fullWidth error={!!formErrors.module_ids}>
                <InputLabel id="modules-label">Modules *</InputLabel>
                <Select
                  labelId="modules-label"
                  multiple
                  name="module_ids"
                  value={formData.module_ids}
                  onChange={handleFormChange as any}
                  input={<OutlinedInput label="Modules *" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(selected as string[]).map((value) => {
                        const module = modules.find((m) => m._id === value);
                        return <Chip key={value} label={module?.module_title || value} size="small" />;
                      })}
                    </Box>
                  )}
                >
                  {modules.map((module) => (
                    <MenuItem key={module._id} value={module._id}>
                      {module.module_title}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.module_ids && <FormHelperText>{formErrors.module_ids}</FormHelperText>}
              </FormControl>
            </Grid>

            {/* Base Price */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Base Price"
                name="base_price"
                value={formData.base_price}
                onChange={handleNumberChange}
                error={!!formErrors.base_price}
                helperText={formErrors.base_price}
                required
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
            </Grid>

            {/* Discount Type */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="discount-type-label">Discount Type</InputLabel>
                <Select
                  labelId="discount-type-label"
                  name="discount_type"
                  value={formData.discount_type}
                  onChange={handleFormChange as any}
                  label="Discount Type"
                >
                  <MenuItem value="none">No Discount</MenuItem>
                  <MenuItem value="percentage">Percentage</MenuItem>
                  <MenuItem value="fixed">Fixed Amount</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Discount Value */}
            {formData.discount_type !== 'none' && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Discount Value"
                  name="discount_value"
                  value={formData.discount_value}
                  onChange={handleNumberChange}
                  error={!!formErrors.discount_value}
                  helperText={formErrors.discount_value}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {formData.discount_type === 'percentage' ? '%' : '$'}
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            )}

            {/* Final Price Display */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Final Price"
                value={`$${calculateFinalPrice(
                  formData.base_price,
                  formData.discount_type,
                  formData.discount_value
                ).toFixed(2)}`}
                InputProps={{
                  readOnly: true,
                }}
                disabled
              />
            </Grid>

            {/* Free Plan Switch */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_free_plan}
                    onChange={handleSwitchChange}
                    name="is_free_plan"
                    color="info"
                  />
                }
                label="Free Plan (No cost to users)"
              />
            </Grid>

            {/* Has Limits Switch */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_any_limit}
                    onChange={handleSwitchChange}
                    name="is_any_limit"
                  />
                }
                label="Enable Usage Limits"
              />
            </Grid>

            {/* Limit Fields */}
            {formData.is_any_limit && (
              <>
                {/* <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Search Limit"
                    name="search_limit"
                    value={formData.search_limit}
                    onChange={handleNumberChange}
                    error={!!formErrors.search_limit}
                    helperText={formErrors.search_limit}
                    required
                  />
                </Grid> */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Resume Limit"
                    name="resume_limit"
                    value={formData.resume_limit}
                    onChange={handleNumberChange}
                    error={!!formErrors.resume_limit}
                    helperText={formErrors.resume_limit}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Cover Letter Limit"
                    name="cover_letter_limit"
                    value={formData.cover_letter_limit}
                    onChange={handleNumberChange}
                    error={!!formErrors.cover_letter_limit}
                    helperText={formErrors.cover_letter_limit}
                    required
                  />
                </Grid>
                {/* <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Manual Apply Limit"
                    name="job_apply_limit"
                    value={formData.job_apply_limit}
                    onChange={handleNumberChange}
                    error={!!formErrors.job_apply_limit}
                    helperText={formErrors.job_apply_limit}
                    required
                  />
                </Grid> */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Auto Apply Limit"
                    name="auto_apply_limit"
                    value={formData.auto_apply_limit}
                    onChange={handleNumberChange}
                    error={!!formErrors.auto_apply_limit}
                    helperText={formErrors.auto_apply_limit}
                    required
                  />
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeFormDialog}>Cancel</Button>
          <Button disabled={submitLoader} variant="contained" onClick={handleSavePlan}>
            { submitLoader ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography id="delete-dialog-description">
            Are you sure you want to delete the plan "{planToDelete?.title}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog}>Cancel</Button>
          <Button onClick={handleDeletePlan} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Plan Details Modal */}
      <Dialog
        open={viewModalOpen}
        onClose={handleCloseViewModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '90vh',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Plan Details
          </Typography>
          <IconButton onClick={handleCloseViewModal} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ mt: 2 }}>
          {selectedPlan && (
            <Box>
              {/* Title and Status */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {selectedPlan.title}
                  </Typography>
                  {selectedPlan.is_free_plan && (
                    <Chip label="Free Plan" color="info" />
                  )}
                  {renderStatusChip(selectedPlan.status)}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Last updated: {formatDate(selectedPlan.updatedAt)}
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Pricing Information */}
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Pricing Information
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4}>
                  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      Base Price
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      ${selectedPlan.base_price.toFixed(2)}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      Discount
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {selectedPlan.discount_type === 'none'
                        ? 'None'
                        : selectedPlan.discount_type === 'percentage'
                          ? `${selectedPlan.discount_value}%`
                          : `$${selectedPlan.discount_value}`}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Discounted Price
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                      ${selectedPlan.discounted_price.toFixed(2)}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Usage Limits */}
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Usage Limits
              </Typography>
              {selectedPlan.is_any_limit ? (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={6}>
                    <Box
                      sx={{
                        p: 2,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 1,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Search Limit
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {selectedPlan.search_limit}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box
                      sx={{
                        p: 2,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 1,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Resume Limit
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {selectedPlan.resume_limit}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box
                      sx={{
                        p: 2,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 1,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Manual Apply Limit
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {selectedPlan.job_apply_limit}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box
                      sx={{
                        p: 2,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 1,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Auto Apply Limit
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {selectedPlan.auto_apply_limit}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              ) : (
                <Box sx={{ mb: 3 }}>
                  <Chip label="Unlimited Usage - No Limits" color="success" />
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Modules */}
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Included Modules
              </Typography>
              <Box sx={{ mb: 2 }}>
                {selectedPlan.module_ids && selectedPlan.module_ids.length > 0 ? (
                  <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                    {selectedPlan.module_ids.map((moduleId, index) => {
                      const module = modules.find((m) => m._id === moduleId);
                      return (
                        <Chip
                          key={index}
                          label={module?.module_title || moduleId}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      );
                    })}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No modules included
                  </Typography>
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Additional Information */}
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Additional Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Plan ID
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {selectedPlan._id}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Discount Type
                  </Typography>
                  <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                    {selectedPlan.discount_type}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Button onClick={handleCloseViewModal} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Alert Snackbar */}
      <Snackbar
        open={alert.open}
        autoHideDuration={5000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseAlert} severity={alert.severity} sx={{ width: '100%' }}>
          {alert.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Plans;
